import React, { useState } from "react";
import { Checkbox } from "@mui/material";
import { useSelector } from "react-redux";
import {
	CustomIconButton,
	CustomTableCell,
	CustomTableRow,
	CustomTooltip,
	icon,
	VersionComparisonPanel,
} from "../../../../../../common/components";
import { DocumentService } from "../../../../../../api";
import { useApi } from "../../../../../../common/hooks";
import { translate, translateDate } from "../../../../../../common/providers";
import styles from "../TableRow.module.css";
import { VERSIONING_STATUS } from "../../../../constants/constants";

const BulkTableBodyRow = ({
	disabled = false,
	disableStatusSelection = false,
	isChecked,
	isInversed,
	onChangeStatus,
	onInverse,
	onSelect,
	row,
	status,
}) => {
	const projectId = useSelector(({ context }) => context.project.id);
	const { call: getAllDocVersionCandidateIds } = useApi(DocumentService.getDocumentPath);
	const [path, setPath] = useState("");
	const [hoveredLeft, setHoveredLeft] = useState(false);
	const [hoveredRight, setHoveredRight] = useState(false);
	const [comparisonOpen, setComparisonOpen] = useState(false);
	const [comparisonData, setComparisonData] = useState(null);
	const handleSelect = () => {
		onSelect(isChecked, row.candidateId);
	};
	const handleHoverLeft = (documentId) => {
		setPath("");
		setHoveredLeft(true);
		getAllDocVersionCandidateIds({ documentId })
			.then((data) => {
				setPath(data.value);
			})
			.catch(console.error);
	};
	const handleHoverRight = (documentId) => {
		setPath("");
		setHoveredRight(true);
		getAllDocVersionCandidateIds({ documentId })
			.then((data) => {
				setPath(data.value);
			})
			.catch(console.error);
	};

	const handleLeaveLeftRow = () => {
		setHoveredLeft(false);
	};
	const handleLeaveRightRow = () => {
		setHoveredRight(false);
	};

	const handleCompare = () => {
		const data = {
			currentDocId: isInversed ? row.oldDocId : row.newDocId,
			currentDocName: isInversed ? row.oldDocName : row.newDocName,
			previousDocId: isInversed ? row.newDocId : row.oldDocId,
			previousDocName: isInversed ? row.newDocName : row.oldDocName,
		};
		setComparisonData(data);
		setComparisonOpen(true);
	};

	const handleCloseComparison = () => {
		setComparisonOpen(false);
	};

	const leftDocName = isInversed ? row.newDocName : row.oldDocName;
	const rightDocName = isInversed ? row.oldDocName : row.newDocName;
	const leftTitle = hoveredLeft && path ? `${path} / ${leftDocName}` : leftDocName;
	const rightTitle = hoveredRight && path ? `${path} / ${rightDocName}` : rightDocName;

	return (
		<>
			<CustomTableRow>
				<CustomTableCell align="center" className={`${styles.cell} ${styles["cell__width--48px"]}`}>
					<Checkbox checked={isChecked} disabled={disabled} onClick={handleSelect} />
				</CustomTableCell>
				<CustomTableCell className={`${styles.cell} ${styles["cell--greyed"]} ${styles["cell__width--date"]}`}>
					{isInversed ? translateDate(row.newDocUploaded) : translateDate(row.oldDocUploaded)}
				</CustomTableCell>
				<CustomTableCell className={`${styles.cell} ${styles["cell--greyed"]} ${styles["cell__width--text"]}`}>
					<div
						className={styles["cell--file-name"]}
						data-hovered={hoveredLeft}
						role="presentation"
						onMouseEnter={() => {
							handleHoverLeft(isInversed ? row.newDocId : row.oldDocId);
						}}
						onMouseLeave={handleLeaveLeftRow}
					>
						<CustomIconButton
							iconOnly
							icon={icon.faFolderTree}
							iconColor="var(--grey-300)"
							size="sm"
							variant="ghost"
						/>
						<CustomTooltip placement="bottom-start" title={leftTitle}>
							<span>{leftDocName}</span>
						</CustomTooltip>
					</div>
				</CustomTableCell>
				<CustomTableCell className={`${styles.cell} ${styles["cell__width--date"]}`}>
					{isInversed ? translateDate(row.oldDocUploaded) : translateDate(row.newDocUploaded)}
				</CustomTableCell>
				<CustomTableCell className={`${styles.cell} ${styles["cell__width--text"]}`}>
					<div
						className={styles["cell--file-name"]}
						data-hovered={hoveredRight}
						role="presentation"
						onMouseEnter={() => {
							handleHoverRight(isInversed ? row.oldDocId : row.newDocId);
						}}
						onMouseLeave={handleLeaveRightRow}
					>
						<CustomIconButton
							iconOnly
							icon={icon.faFolderTree}
							iconColor="var(--grey-300)"
							size="sm"
							variant="ghost"
						/>
						<CustomTooltip placement="bottom-start" title={rightTitle}>
							<span>{rightDocName}</span>
						</CustomTooltip>
					</div>
				</CustomTableCell>
				<CustomTableCell align="center" className={`${styles.cell} ${styles["cell__width--60px"]}`}>
					<CustomIconButton
						icon={icon.faCopy}
						iconColor="var(--grey-700)"
						size="sm"
						tooltip={translate("document-center.table.bulk-versioning-dialog.table.compare-documents")}
						variant="outlined"
						onClick={handleCompare}
					/>
				</CustomTableCell>
				<CustomTableCell align="center" className={`${styles.cell} ${styles["cell__width--60px"]}`}>
					<CustomIconButton
						disabled={row.oldDocHasVersion || row.oldDocHasGeneratedContent}
						icon={icon.faArrowRightArrowLeft}
						iconColor="var(--grey-700)"
						size="sm"
						tooltip={translate("document-center.table.bulk-versioning-dialog.table.inverse-documents")}
						variant="outlined"
						onClick={() => onInverse(row.candidateId)}
					/>
				</CustomTableCell>
				<CustomTableCell align="center" className={`${styles.cell} ${styles["cell__width--action"]}`}>
					<Checkbox
						checked={status === VERSIONING_STATUS.VALIDATED}
						disabled={disableStatusSelection}
						onClick={() => onChangeStatus(row.candidateId, VERSIONING_STATUS.VALIDATED)}
					/>
				</CustomTableCell>
				<CustomTableCell align="center" className={`${styles.cell} ${styles["cell__width--action"]}`}>
					<Checkbox
						checked={status === VERSIONING_STATUS.DISCARDED}
						disabled={disableStatusSelection}
						onClick={() => onChangeStatus(row.candidateId, VERSIONING_STATUS.DISCARDED)}
					/>
				</CustomTableCell>
				<CustomTableCell align="center" className={`${styles.cell} ${styles["cell__width--action"]}`}>
					<Checkbox
						checked={!status || status === VERSIONING_STATUS.CREATED}
						disabled={disableStatusSelection}
						onClick={() => onChangeStatus(row.candidateId, VERSIONING_STATUS.CREATED)}
					/>
				</CustomTableCell>
			</CustomTableRow>

			<VersionComparisonPanel
				currentDocId={comparisonData?.currentDocId}
				currentDocName={comparisonData?.currentDocName}
				enableLeftApiCall={false}
				enableRightApiCall={false}
				open={comparisonOpen}
				previousDocId={comparisonData?.previousDocId}
				previousDocName={comparisonData?.previousDocName}
				projectId={projectId}
				onClose={handleCloseComparison}
			/>
		</>
	);
};

export default BulkTableBodyRow;
